import { createClient } from '@supabase/supabase-js';
import { showToast } from './utils/toast';

// Preview links (any *.vercel.app address: branches we're trying out) always
// use the STAGING database, whatever the build settings say, so a preview can
// never touch real data. heytemis.com uses the build's database (production).
// The anon key is the public one every browser receives anyway.
const STAGING = {
  url: "https://sqjszokeszwzbwcevhko.supabase.co",
  anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNxanN6b2tlc3p3emJ3Y2V2aGtvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTI2MjE2MzMsImV4cCI6MjEwODE5NzYzM30.F-P9S07dy5unZn1AEu5gIiKFfkRsVtRvvNyT4TmM0Wg",
};
const onPreviewSite = /\.vercel\.app$/i.test(window.location.hostname);
const supabaseUrl = onPreviewSite ? STAGING.url : process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = onPreviewSite ? STAGING.anonKey : process.env.REACT_APP_SUPABASE_ANON_KEY;
// True on preview links and on local runs pointed at staging.
export const isStaging = supabaseUrl === STAGING.url;

// Capture before the SDK consumes and clears an email-link fragment.
export const passwordFlowFromUrl = /(?:^#|&)type=(?:recovery|invite)(?:&|$)/.test(window.location.hash);

// ── Failed-request reporting ──────────────────────────────────────────────
// Many call sites read `{ data }` and ignore `error`, so a failed query used to
// render as empty data (e.g. every name showing "Unknown"). Every database
// request now passes through here: failed reads tell the user, and every
// failure is recorded in client_errors so it can be found and fixed.
const lastReported = new Map();
let lastToastAt = 0;

function reportFailure(url, method, res) {
  const path = url.pathname.replace(/^\/rest\/v1\//, "");
  if (path.startsWith("client_errors")) return; // never report the reporter
  const table = path.split("?")[0];
  const key = `${method} ${table} ${res.status}`;
  const now = Date.now();
  if (now - (lastReported.get(key) ?? 0) < 60_000) return; // one report per kind per minute
  lastReported.set(key, now);

  res.clone().json().catch(() => ({})).then((body) => {
    const message = body?.message || body?.error || res.statusText;
    console.error(`[supabase] ${method} ${table} failed (${res.status})`, message, body);

    if (method === "GET" && now - lastToastAt > 10_000) {
      lastToastAt = now;
      showToast({ kind: "error", title: "Some data didn't load", body: "Refresh the page. If it keeps happening, let us know." });
    }

    supabase.from("client_errors").insert({
      method,
      path: (table + url.search).slice(0, 500),
      status: res.status,
      code: body?.code ?? null,
      message: String(message ?? "").slice(0, 1000),
      page: window.location.hash.slice(0, 200),
      app_version: process.env.REACT_APP_VERSION ?? null,
    }).then(() => {}, () => {});
  });
}

async function trackedFetch(input, init) {
  const res = await fetch(input, init);
  if (!res.ok) {
    const url = new URL(typeof input === "string" ? input : input.url);
    if (url.pathname.startsWith("/rest/v1/")) {
      reportFailure(url, (init?.method || (typeof input === "string" ? "GET" : input.method) || "GET").toUpperCase(), res);
    }
  }
  return res;
}

// eventsPerSecond raises the client's realtime throughput ceiling from the default
// of 10/s so bursts (several task edits, multiple users at once) aren't queued.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  realtime: { params: { eventsPerSecond: 40 } },
  global: { fetch: trackedFetch },
});
